import React, { useState, useEffect } from 'react';

// Validation libraries
import { useFormik } from 'formik';
import * as yup from 'yup';

// Hooks
import { useDispatch } from 'react-redux';

// Actions
import { createCustomField } from '~/redux/actions/customFields-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Utils
import { MESSAGES } from '~/utils/message';
import { validateForm, realTimeValidation, resetValidation } from '~/utils/formik-validation';
import { DEFAULT_PAGINATION } from '~/utils/constants';

// MUI Components
import Typography from '@mui/material/Typography';
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';

// styles
import './styles.scss';
import OlcButton from '~/components/General/OlcButton';
import OlcFormModal from '~/components/General/OlcFormModal';

const DEFAULT_CUSTOM_FIELD_TYPE = 'single_text';
const URL_CUSTOM_FIELD_TYPE = 'url';

const CreateModal = (props) => {
  const formId = 'create-custom-field-form';
  const {
    openModal,
    handleCloseModal,
    pagination,
    updatePagination,
    onLimitReached,
    onSuccess,
    customFieldTypes = [],
    defaultType = DEFAULT_CUSTOM_FIELD_TYPE,
    disableType = false,
  } = props;

  const [loading, setLoading] = useState(false);
  const customFieldTypeOptions = customFieldTypes.some((fieldType) => fieldType.key === defaultType)
    ? customFieldTypes
    : [
        ...customFieldTypes,
        {
          key: defaultType,
          title: defaultType === URL_CUSTOM_FIELD_TYPE ? 'URL' : defaultType,
        },
      ];

  const onClose = () => {
    handleCloseModal();
    formik.handleReset();
    resetValidation();
    setLoading(false);
  };

  const dispatch = useDispatch();

  const validationSchema = yup.object().shape({
    name: yup
      .string()
      .trim()
      .required(MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_REQUIRED)
      .min(1, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_REQUIRED)
      .max(25, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_NAME_LIMIT)
      .matches(/^[a-zA-Z0-9 ]*$/, MESSAGES.SETTINGS.CUSTOM_FIELDS.CUSTOM_FIELD_VALIDATION),
    type: yup.string().required(MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE_REQUIRED),
  });

  const formik = useFormik({
    initialValues: {
      name: '',
      type: defaultType,
    },
    validationSchema: validationSchema,
  });

  const createNewCustomField = async (event) => {
    try {
      setLoading(true);
      event.preventDefault();
      const isValid = validateForm(formik);
      if (!isValid) {
        setLoading(false);
        return;
      }

      const payload = {
        customFieldName: formik.values.name,
        type: formik.values.type,
      };

      const response = await dispatch(createCustomField(payload, pagination));

      if (response.status === 200) {
        dispatch(success(response.data.message));
        await onSuccess?.(response, payload);
        updatePagination(DEFAULT_PAGINATION);
        onClose();
      } else {
        if (response.status === 418) {
          if (onLimitReached) {
            const errorMessage = response?.data?.message || '';
            onClose();
            onLimitReached(errorMessage);
          }
          return;
        }
        dispatch(
          failure(
            response.data.data?.errors?.length
              ? response.data.data?.errors[0].msg
              : response.data?.message
          )
        );
      }
    } catch (error) {
      setLoading(false);

      // Handle 418 error specifically for limit reached
      if (error?.response?.status === 418) {
        if (onLimitReached) {
          const errorMessage = error?.response?.data?.message || (
            <>
              <p>You've reached your custom field limit!</p>
              <p>Please upgrade your subscription to add more custom fields.</p>
            </>
          );
          onClose();
          onLimitReached(errorMessage);
        }
        return;
      }
      // Handle field already exists error
      if (
        error?.response?.data?.message === MESSAGES.SETTINGS.CUSTOM_FIELDS.RESPONSE_ALREADY_EXIST
      ) {
        formik.setErrors({
          name: MESSAGES.SETTINGS.CUSTOM_FIELDS.FIELD_EXISTS,
        });
        return;
      }

      // For other errors, dispatch failure message
      dispatch(
        failure(
          error?.response?.data?.message ||
            error?.message ||
            MESSAGES.GENERAL_ERROR ||
            'An error occurred. Please try again.'
        )
      );
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (openModal) {
      formik.setFieldValue('type', defaultType, false);
      return;
    }

    if (!openModal) {
      // Reset all state when modal closes
      formik.handleReset();
      resetValidation();
      setLoading(false);
    }
  }, [openModal]);

  return (
    <OlcFormModal
      open={openModal}
      onClose={onClose}
      title={MESSAGES.SETTINGS.CUSTOM_FIELDS?.CREATE_CUSTOM_FIELD}
      className="customFieldFormModal"
      footer={
        <>
          <OlcButton variant="secondary" type="button" onClick={onClose} disabled={loading}>
            {MESSAGES.ACCOUNT.CANCEL_BUTTON}
          </OlcButton>
          <OlcButton
            variant="primary"
            type="submit"
            form={formId}
            onClick={() => validateForm(formik)}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress
                sx={{
                  color: 'white',
                  width: '20px !important',
                  height: '20px !important',
                }}
              />
            ) : (
              MESSAGES.SETTINGS.CUSTOM_FIELDS.CREATE
            )}
          </OlcButton>
        </>
      }
    >
      <Box className="modalFormWrapper">
        <form id={formId} onSubmit={createNewCustomField} noValidate>
          <Box className="modalFlexArea">
            <Box>
              <InputLabel htmlFor="Name" className="formLabel">
                {MESSAGES.SETTINGS.CUSTOM_FIELDS?.NAME}
                <span className="olcRequired">*</span>
              </InputLabel>
              <TextField
                placeholder={MESSAGES.SETTINGS.CUSTOM_FIELDS?.PLACEHOLDER}
                fullWidth
                name="name"
                className={
                  formik.errors.name && formik.touched.name
                    ? 'invalid formInput inputField formField'
                    : 'inputField formField'
                }
                sx={{
                  '& input::placeholder': {
                    color: '#000',
                    fontSize: '14px',
                    fontWeight: '400',
                    lineHeight: 'normal',
                    fontFamily: 'Inter',
                    fontStyle: 'normal',
                  },
                  outline: 'none',
                  '&:focus': {
                    outline: 'none',
                  },
                }}
                onChange={formik.handleChange}
                value={formik.values.name.trimStart()}
                onBlur={realTimeValidation ? formik.handleBlur : undefined}
                autoComplete="off"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end" sx={{ width: '16px', marginRight: '8px' }}>
                      {formik.values.name && (
                        <IconButton
                          className="cpClearInputBtn"
                          size="small"
                          onClick={() => {
                            formik.setFieldValue('name', '');
                            formik.setFieldTouched('name', true);
                          }}
                          edge="end"
                          sx={{ padding: '4px' }}
                        >
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      )}
                    </InputAdornment>
                  ),
                }}
              />
              {formik.touched.name && formik.errors.name ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.name}
                </Typography>
              ) : null}
            </Box>
            <Box>
              <InputLabel htmlFor="type" className="formLabel">
                {MESSAGES.SETTINGS.CUSTOM_FIELDS.TYPE}
                <span className="olcRequired">*</span>
              </InputLabel>
              <FormControl fullWidth>
                <Select
                  id="type"
                  name="type"
                  value={formik.values.type}
                  onChange={formik.handleChange}
                  onBlur={realTimeValidation ? formik.handleBlur : undefined}
                  className="inputField formField customFieldTypeSelect"
                  disabled={disableType}
                  MenuProps={{
                    PaperProps: {
                      className: 'customFieldTypeMenu',
                    },
                  }}
                >
                  {customFieldTypeOptions.map((fieldType) => (
                    <MenuItem key={fieldType.key} value={fieldType.key}>
                      {fieldType.title}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {formik.errors.type ? (
                <Typography className="errorMessage">
                  <sup>*</sup>
                  {formik.errors.type}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </form>
      </Box>
    </OlcFormModal>
  );
};

export default CreateModal;
